
import { useState } from 'react';
import type { Track, Playlist } from '../page';
import SafeImage from './SafeImage';

interface RecommendationsProps {
  setCurrentTrack: (track: Track) => void;
  onAddToPlaylist: (playlistId: number, track: Track) => void;
  playlists: Playlist[];
  availableTracks: Track[];
}

export default function Recommendations({ setCurrentTrack, onAddToPlaylist, playlists, availableTracks }: RecommendationsProps) {
  const [showAddToPlaylist, setShowAddToPlaylist] = useState<number | null>(null);
  const recommendations = availableTracks;

  const handleAddToPlaylist = (playlistId: number, track: Track) => {
    onAddToPlaylist(playlistId, track);
    setShowAddToPlaylist(null);
  };

  return (
    <div>
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 mb-6 shadow-sm">
        <h2 className="text-lg font-bold text-gray-800 mb-2">Discover New Music</h2>
        <p className="text-gray-600 text-sm">Fresh African hits and trending songs</p>
      </div>

      {/* Trending Section */}
      <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 mb-6 shadow-sm">
        <h3 className="font-semibold text-gray-800 mb-4">🔥 Trending Now</h3>
        {recommendations.length === 0 ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 flex items-center justify-center bg-stone-100 rounded-full mx-auto mb-4">
              <i className="ri-music-line text-stone-400 text-2xl"></i>
            </div>
            <p className="text-gray-600 text-sm font-medium">No tracks in the catalog yet</p>
            <p className="text-gray-500 text-xs">Check back later or add tracks from the admin</p>
          </div>
        ) : (
        <div className="space-y-3">
          {recommendations.slice(0, 3).map((track) => (
            <div 
              key={track.id}
              className="flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors"
            >
              <div className="cursor-pointer shrink-0" onClick={() => setCurrentTrack(track)}>
                <SafeImage src={track.image} alt={track.title} size="sm" />
              </div>
              <div className="flex-1 min-w-0 cursor-pointer" onClick={() => setCurrentTrack(track)}>
                <h4 className="font-medium text-sm text-gray-800 truncate">{track.title}</h4>
                <p className="text-xs text-gray-600 truncate">{track.artist}</p>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500">{track.duration}</span>
                <button
                  onClick={() => setShowAddToPlaylist(track.id)}
                  className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-orange-500 transition-colors"
                >
                  <i className="ri-add-line text-sm"></i>
                </button>
              </div>
            </div>
          ))}
        </div>
        )}
      </div>

      {/* New Releases */}
      <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 shadow-sm">
        <h3 className="font-semibold text-gray-800 mb-4">🎵 New Releases</h3>
        {recommendations.length <= 3 ? (
          recommendations.length === 0 ? null : (
          <div className="text-center py-6">
            <p className="text-gray-500 text-sm">More tracks will appear here as they’re added</p>
          </div>
          )
        ) : (
        <div className="space-y-3">
          {recommendations.slice(3).map((track) => (
            <div 
              key={track.id}
              className="flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors"
            >
              <div className="cursor-pointer shrink-0" onClick={() => setCurrentTrack(track)}>
                <SafeImage src={track.image} alt={track.title} size="sm" />
              </div>
              <div className="flex-1 min-w-0 cursor-pointer" onClick={() => setCurrentTrack(track)}>
                <h4 className="font-medium text-sm text-gray-800 truncate">{track.title}</h4>
                <p className="text-xs text-gray-600 truncate">{track.artist}</p>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500">{track.duration}</span>
                <button
                  onClick={() => setShowAddToPlaylist(track.id)}
                  className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-orange-500 transition-colors"
                >
                  <i className="ri-add-line text-sm"></i>
                </button>
              </div>
            </div>
          ))}
        </div>
        )}
      </div>

      {/* Add to Playlist Modal */}
      {showAddToPlaylist && (
        <div className="fixed inset-0 bg-black/50 flex items-end z-50">
          <div className="bg-white rounded-t-2xl w-full max-h-[50vh] overflow-hidden">
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-gray-800">Add to Playlist</h3>
                <button
                  onClick={() => setShowAddToPlaylist(null)}
                  className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-600"
                >
                  <i className="ri-close-line text-sm"></i>
                </button>
              </div>
            </div>
            
            <div className="p-4 overflow-y-auto max-h-[calc(50vh-80px)]">
              {playlists.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-600 text-sm font-medium">No playlists yet</p>
                  <p className="text-gray-500 text-xs">Create one from the Manage tab first</p>
                </div>
              ) : (
              <div className="space-y-3">
                {playlists.map((playlist) => {
                  const track = recommendations.find(t => t.id === showAddToPlaylist);
                  const isTrackInPlaylist = track && playlist.tracks.some(t => t.id === track.id);
                  
                  return (
                    <div 
                      key={playlist.id}
                      onClick={() => track && !isTrackInPlaylist && handleAddToPlaylist(playlist.id, track)}
                      className={`flex items-center gap-3 p-3 rounded-xl transition-colors ${
                        isTrackInPlaylist 
                          ? 'bg-gray-100 cursor-not-allowed opacity-50' 
                          : 'hover:bg-gray-50 cursor-pointer'
                      }`}
                    >
                      <SafeImage src={playlist.coverImage} alt={playlist.name} size="sm" />
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-sm text-gray-800 truncate">{playlist.name}</h4>
                        <p className="text-xs text-gray-600 truncate">{playlist.tracks.length} songs</p>
                      </div>
                      <div className="w-6 h-6 flex items-center justify-center">
                        {isTrackInPlaylist ? (
                          <i className="ri-check-line text-green-500 text-sm"></i>
                        ) : (
                          <i className="ri-add-line text-orange-500 text-sm"></i>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
